import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'

/**
 * 主题管理 Store
 * 负责管理应用的主题模式（浅色/深色/跟随系统）
 */
export const useThemeStore = defineStore(
  'theme',
  () => {
    // State
    const mode = ref<ThemeMode>('system')
    const systemTheme = ref<'light' | 'dark'>('light')

    // Helper: 获取当前系统主题
    const getSystemTheme = () =>
      window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

    // Init system theme
    systemTheme.value = getSystemTheme()

    // Listener: 监听系统主题变化
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      systemTheme.value = e.matches ? 'dark' : 'light'
    })

    // Getters
    // 计算最终是否为深色模式
    const isDark = computed(() => {
      if (mode.value === 'system') {
        return systemTheme.value === 'dark'
      }
      return mode.value === 'dark'
    })

    // Actions
    function updateDOM() {
      const resolvedTheme = isDark.value ? 'dark' : 'light'

      document.documentElement.classList.toggle('dark', isDark.value)
      document.documentElement.setAttribute('data-immersive-translate-page-theme', resolvedTheme)
    }

    /**
     * 设置主题模式
     * @param newMode 'light' | 'dark' | 'system'
     */
    function setTheme(newMode: ThemeMode) {
      mode.value = newMode
    }

    /**
     * 循环切换主题模式
     * Light -> Dark -> System -> Light
     */
    function cycleTheme() {
      if (mode.value === 'light') setTheme('dark')
      else if (mode.value === 'dark') setTheme('system')
      else setTheme('light')
    }

    // Watchers
    // 监听 isDark 变化并同步 DOM
    watch(
      isDark,
      () => {
        updateDOM()
      },
      { immediate: true },
    )

    return {
      mode,
      isDark,
      setTheme,
      cycleTheme,
    }
  },
  {
    persist: {
      key: 'rapoo-theme',
      pick: ['mode'],
    },
  },
)
